import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const TITOLARE = {
  nome: "Dinamika A.P.S.",
  cf: "92051380886",
  sede: "Via G. Marconi n. 38, 97012 Chiaramonte Gulfi (RG)",
  email: "dinamikaaps@gmail.com",
};

function LegalShell({ title, updated, children, testid }) {
  return (
    <div className="min-h-screen bg-[#121214] text-gray-300" data-testid={testid}>
      <div className="checkered-strip" />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <Link
          to="/login"
          data-testid="legal-back-link"
          className="inline-flex items-center gap-2 font-body-sport text-xs font-bold tracking-widest uppercase text-gray-400 hover:text-[#FF1E27] transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Torna a FantasyRace
        </Link>
        <h1 className="font-racing font-black text-3xl uppercase tracking-wide text-white mb-1">{title}</h1>
        <p className="text-xs text-gray-500 font-body-sport mb-8">Ultimo aggiornamento: {updated}</p>
        <div className="space-y-6 text-sm leading-relaxed font-body-sport">{children}</div>

        <div className="mt-12 rounded-xl border border-[#3F3F4C] bg-[#1A1A1E] p-5 text-xs text-gray-400 space-y-1">
          <p className="font-body-sport font-bold text-white uppercase tracking-widest text-[10px] mb-2">Titolare del trattamento</p>
          <p>{TITOLARE.nome} — C.F. {TITOLARE.cf}</p>
          <p>{TITOLARE.sede}</p>
          <p>
            Email:{" "}
            <a href={`mailto:${TITOLARE.email}`} className="text-[#FFB703] hover:underline">
              {TITOLARE.email}
            </a>
          </p>
          <p className="pt-2 space-x-3">
            <Link to="/privacy" className="text-gray-300 hover:text-white underline underline-offset-2">Privacy Policy</Link>
            <Link to="/cookie-policy" className="text-gray-300 hover:text-white underline underline-offset-2">Cookie Policy</Link>
            <Link to="/termini" className="text-gray-300 hover:text-white underline underline-offset-2">Termini e Condizioni</Link>
            <button
              data-testid="cookie-preferences-button"
              onClick={() => window.dispatchEvent(new Event("fr-open-cookie-preferences"))}
              className="text-gray-300 hover:text-white underline underline-offset-2"
            >
              Rivedi le preferenze Cookie
            </button>
          </p>
        </div>
      </div>
      <div className="checkered-strip" />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-racing font-bold text-base uppercase tracking-wide text-white mb-2">{title}</h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

export function PrivacyPolicy() {
  return (
    <LegalShell title="Privacy Policy" updated="Ottobre 2026" testid="legal-privacy">
      <Section title="1. Titolare del trattamento (Art. 13 GDPR)">
        <p>
          Il titolare del trattamento dei dati personali è <strong className="text-white">{TITOLARE.nome}</strong> (C.F. {TITOLARE.cf}),
          con sede legale in {TITOLARE.sede}. Per qualsiasi richiesta relativa alla privacy:{" "}
          <a href={`mailto:${TITOLARE.email}`} className="text-[#FFB703] hover:underline">{TITOLARE.email}</a>.
        </p>
      </Section>
      <Section title="2. Tipologia di dati raccolti">
        <p><strong className="text-white">Dati di registrazione:</strong> username, indirizzo email, nome della scuderia, password (mai salvata in chiaro — vedi sezione 5).</p>
        <p><strong className="text-white">Dati di gioco:</strong> composizione della squadra, punteggi, trofei, leghe create o a cui si partecipa, messaggi pubblicati nelle bacheche delle leghe.</p>
        <p><strong className="text-white">Dati del dispositivo e tecnici:</strong> tipo di dispositivo e browser, log di sistema, indirizzi IP e tentativi di accesso a fini di sicurezza, identificatori delle sottoscrizioni alle notifiche push (solo previo consenso), preferenze di consenso cookie.</p>
      </Section>
      <Section title="3. Finalità, base giuridica e natura del conferimento">
        <p>I dati sono trattati per: (a) erogare il servizio di fantasy game (esecuzione del contratto, art. 6.1.b GDPR); (b) sicurezza della piattaforma e prevenzione degli abusi (legittimo interesse, art. 6.1.f GDPR); (c) notifiche push di gioco e contenuti pubblicitari di terze parti, solo dopo consenso esplicito (art. 6.1.a GDPR).</p>
        <p><strong className="text-white">Il conferimento dei dati di registrazione è obbligatorio:</strong> in mancanza non è possibile creare un account né partecipare al gioco. Il conferimento dei dati per finalità marketing è invece facoltativo e il rifiuto non pregiudica l'uso della piattaforma.</p>
      </Section>
      <Section title="4. Hosting e fornitori cloud">
        <p>
          I dati sono ospitati e processati tramite le infrastrutture cloud sicure di <strong className="text-white">Emergent</strong> e{" "}
          <strong className="text-white">Cloudflare</strong>, che operano come responsabili del trattamento. Per eventuali trasferimenti di dati
          extra-UE sono adottate garanzie adeguate ai sensi degli artt. 44-49 GDPR, quali Clausole Contrattuali Tipo (SCC) o il
          EU-US Data Privacy Framework.
        </p>
      </Section>
      <Section title="5. Sicurezza delle password">
        <p>
          <strong className="text-white">Le password non sono mai salvate in chiaro</strong>, ma vengono cifrate/protette tramite funzioni di
          hashing crittografico dedicate (bcrypt/argon2 con salt univoco). Nessun operatore dell'associazione può leggere la tua password.
        </p>
      </Section>
      <Section title="6. Conservazione e chiusura dell'account">
        <p>
          I dati identificativi dell'account vengono <strong className="text-white">cancellati entro 30 giorni</strong> dalla richiesta
          dell'utente. I dati anonimizzati relativi alle classifiche e ai punteggi storici rimarranno registrati in forma aggregata
          per preservare l'integrità dello storico delle gare. I log di sicurezza sono conservati per un massimo di 12 mesi.
        </p>
      </Section>
      <Section title="7. Diritti dell'interessato">
        <p>
          Ai sensi degli artt. 15-22 GDPR hai diritto di: accesso, rettifica, cancellazione, limitazione del trattamento, portabilità
          dei dati e opposizione. Per esercitarli scrivi a{" "}
          <a href={`mailto:${TITOLARE.email}`} className="text-[#FFB703] hover:underline">{TITOLARE.email}</a>.
          Hai inoltre diritto di reclamo al Garante per la protezione dei dati personali (www.garanteprivacy.it).
        </p>
      </Section>
      <Section title="8. Cookie">
        <p>
          Per l'uso di cookie e tecnologie di tracciamento consulta la{" "}
          <Link to="/cookie-policy" className="text-[#FFB703] hover:underline">Cookie Policy</Link>.
        </p>
      </Section>
    </LegalShell>
  );
}

export function CookiePolicy() {
  return (
    <LegalShell title="Cookie Policy" updated="Ottobre 2026" testid="legal-cookie">
      <Section title="1. Cosa sono i cookie">
        <p>I cookie e tecnologie simili (es. localStorage) sono piccoli dati salvati sul tuo dispositivo per far funzionare il sito o, previo consenso, per finalità di profilazione pubblicitaria.</p>
      </Section>
      <Section title="2. Cookie e storage tecnici (sempre attivi)">
        <p>Necessari al funzionamento del sito e alla sessione utente: cookie di autenticazione (httpOnly), preferenze di interfaccia, stato del consenso cookie, dati temporanei di gioco. Non richiedono consenso (art. 122 Codice Privacy) e non vengono mai bloccati.</p>
      </Section>
      <Section title="3. Cookie profilanti e di terze parti — Google AdSense">
        <p>
          Per l'erogazione dei banner pubblicitari il sito utilizza Google AdSense, che può installare cookie di profilazione di terza
          parte secondo la propria informativa (policies.google.com/technologies/ads).
        </p>
        <p>
          <strong className="text-white">Blocco preventivo:</strong> gli script pubblicitari e i tracker di terze parti vengono caricati
          <strong className="text-white"> solo dopo il tuo consenso esplicito</strong> tramite il pulsante "Accetta tutti" del Cookie Banner.
          Scegliendo "Solo necessari" nessuno script pubblicitario verrà mai caricato.
        </p>
      </Section>
      <Section title="4. Gestione e revoca del consenso">
        <p>
          Puoi modificare o revocare il consenso in qualsiasi momento con un semplice click sul pulsante{" "}
          <strong className="text-white">"Rivedi le preferenze Cookie"</strong>, sempre disponibile nel footer di ogni pagina del sito:
          il banner ti verrà riproposto e la nuova scelta avrà effetto immediato.
        </p>
      </Section>
    </LegalShell>
  );
}

export function Termini() {
  return (
    <LegalShell title="Termini e Condizioni" updated="Ottobre 2026" testid="legal-termini">
      <Section title="1. Oggetto del servizio">
        <p>
          FantasyRace è un fantasy game organizzato da {TITOLARE.nome} in occasione della 69° Salita dei Monti Iblei.
          L'utilizzo della piattaforma implica l'accettazione integrale dei presenti Termini e Condizioni di Servizio.
        </p>
      </Section>
      <Section title="2. Natura dell'iniziativa e premi">
        <p>
          <strong className="text-white">
            Fantasy Race è un'iniziativa esclusivamente ludica e d'intrattenimento senza scopo di lucro. La partecipazione è gratuita
            e non sono previsti premi in denaro, beni materiali o utili economici aventi valore di mercato. Eventuali riconoscimenti
            hanno carattere puramente simbolico, goliardico o d'onore e non costituiscono in alcun modo manifestazione o concorso a
            premi ai sensi del D.P.R. 430/2001.
          </strong>
        </p>
      </Section>
      <Section title="3. Registrazione, account ed età minima">
        <p>
          L'uso della piattaforma è consentito <strong className="text-white">a tutte le età</strong>. È richiesta la registrazione con
          username, email valida e password. Ogni utente può mantenere un solo account ed è responsabile della riservatezza delle
          proprie credenziali e di ogni attività svolta tramite il proprio account.
        </p>
      </Section>
      <Section title="4. Regolamento di gioco">
        <p>
          Ogni utente compone una scuderia di <strong className="text-white">18 piloti</strong> (massimo 1 per ogni
          Gruppo) con un budget di <strong className="text-white">120 crediti</strong>. Le formazioni si
          bloccano all'orario di chiusura indicato dal countdown; dopo le Prove del Sabato è consentita la sola sostituzione del
          Capitano (i cui punti valgono x1.5).
        </p>
        <p>
          <strong className="text-white">Calcolo dei punteggi:</strong> i punteggi sono calcolati automaticamente sui tempi ufficiali
          della 69° Salita dei Monti Iblei pubblicati dalla Direzione Gara, che costituiscono l'unico riferimento valido: punti per
          le Prove del Sabato (miglior tempo e record di classe), punti per la Gara di Domenica (posizione assoluta e di classe),
          bonus e malus comminati tramite verifica VAR.
        </p>
        <p>
          <strong className="text-white">Pareggi:</strong> in caso di parità di punteggio totale vale l'ex aequo, salvo diversa
          comunicazione ufficiale dell'organizzazione.
        </p>
        <p>
          <strong className="text-white">Aggiornamento classifiche:</strong> le classifiche vengono aggiornate dopo la pubblicazione
          ufficiale dei risultati da parte della Direzione Gara, in genere entro pochi minuti dall'inserimento dei tempi.
        </p>
        <p>
          <strong className="text-white">Contestazioni:</strong> segnalazioni e contestazioni sui punteggi devono essere inviate a{" "}
          <a href={`mailto:${TITOLARE.email}`} className="text-[#FFB703] hover:underline">{TITOLARE.email}</a>{" "}
          <strong className="text-white">entro 48 ore dalla fine della gara</strong>; decorsi tale termine le classifiche si intendono
          definitive. Le decisioni dell'organizzazione sono insindacabili.
        </p>
      </Section>
      <Section title="5. Sospensione e moderazione degli account">
        <p>
          In caso di condotte sleali, spam, tentativi di manipolazione dei punteggi o violazione delle regole della community,
          l'associazione potrà sospendere o disattivare l'account <strong className="text-white">con motivazione formale e previa
          notifica all'utente</strong> all'indirizzo email registrato, salvi i casi di grave e urgente pericolo per la sicurezza
          della piattaforma.
        </p>
      </Section>
      <Section title="6. Regole della community e contenuti degli utenti">
        <p>
          Nelle bacheche delle leghe è vietato pubblicare messaggi contenenti <strong className="text-white">turpiloquio</strong>,
          contenuti illeciti, diffamatori, discriminatori o lesivi del diritto d'autore e dei diritti di terzi. L'utente è l'unico
          responsabile dei contenuti che pubblica. {TITOLARE.nome} si riserva il diritto di <strong className="text-white">moderare
          e rimuovere</strong> qualsiasi contenuto che violi queste regole, anche senza preavviso.
        </p>
      </Section>
      <Section title="7. Condotte vietate">
        <p>È inoltre vietato: creare account multipli, utilizzare bot o strumenti automatizzati, tentare di violare la sicurezza della piattaforma o accedere a dati di altri utenti.</p>
      </Section>
      <Section title="8. Limitazione di responsabilità">
        <p>Il servizio è fornito "così com'è". L'organizzazione non risponde di interruzioni tecniche, errori nei dati di cronometraggio forniti da terzi o modifiche/annullamento della manifestazione reale, in tal caso il gioco potrà essere sospeso senza alcun obbligo di compensazione.</p>
      </Section>
      <Section title="9. Proprietà intellettuale">
        <p>Marchi, loghi, grafica e contenuti della piattaforma sono di proprietà di {TITOLARE.nome} o dei rispettivi titolari. È vietata la riproduzione non autorizzata.</p>
      </Section>
      <Section title="10. Legge applicabile e contatti">
        <p>
          I presenti Termini sono regolati dalla legge italiana. Per qualsiasi richiesta:{" "}
          <a href={`mailto:${TITOLARE.email}`} className="text-[#FFB703] hover:underline">{TITOLARE.email}</a>.
        </p>
      </Section>
    </LegalShell>
  );
}
